import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Bot, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const PROVIDERS = [
  { value: "openai", label: "GPT-5.2 · OpenAI", model: "gpt-5.1" },
  { value: "anthropic", label: "Claude Sonnet 4.5 · Anthropic", model: "claude-sonnet-4-5-20250929" },
  { value: "gemini", label: "Gemini 3 Flash · Google", model: "gemini-3-flash-preview" },
];

export default function AITutorPanel({ open, onClose, courseContext = "" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [sessionId, setSessionId] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (open && user) {
      const sid = `${user.id}__${(courseContext || "general").toLowerCase().replace(/\s+/g, "-")}`;
      setSessionId(sid);
      // load history
      (async () => {
        try {
          const { data } = await api.get(`/ai/history/${sid}`);
          setMessages(
            data.map((m) => ({ role: m.role, content: m.content, model: m.model }))
          );
        } catch (_e) {
          setMessages([]);
        }
      })();
    }
  }, [open, user, courseContext]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId,
        message: msg,
        provider,
        course_context: courseContext,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, model: data.model }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI tutor error");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I couldn't reach the model right now. Please try again or switch to a different provider.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        className="w-full rounded-none border-l-2 border-foreground p-0 sm:max-w-md"
        side="right"
        data-testid="ai-tutor-panel"
      >
        <SheetHeader className="border-b border-border bg-foreground p-5 text-background">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="font-display flex items-center gap-2 text-lg font-extrabold text-background">
                <Sparkles className="h-5 w-5" /> AI Tutor
              </SheetTitle>
              {courseContext && (
                <div className="mono mt-1 text-[10px] uppercase tracking-widest text-background/60">
                  {courseContext}
                </div>
              )}
            </div>
            <button onClick={onClose} className="text-background/80 hover:text-background" data-testid="close-tutor-btn">
              <X className="h-4 w-4" />
            </button>
          </div>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="rounded-none border-background/30 bg-background/10 text-background mono text-[11px] uppercase tracking-widest" data-testid="tutor-provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDERS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SheetHeader>

        <div className="flex h-[calc(100vh-200px)] flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto bg-muted/30 p-5">
            {messages.length === 0 && (
              <div className="rounded-none border border-dashed border-foreground/30 p-6 text-center">
                <Sparkles className="mx-auto h-6 w-6 text-primary" />
                <p className="mt-2 text-sm">
                  Ask anything about your course — concepts, code, math, exam prep.
                </p>
                <div className="mono mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                  Try: "Explain Merkle trees with a simple example"
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-foreground text-background"
                  }`}
                >
                  {m.role === "user" ? <UserIcon className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </div>
                <div
                  className={`max-w-[85%] border bg-background p-3 text-sm ${
                    m.role === "user"
                      ? "border-primary"
                      : "border-foreground"
                  }`}
                >
                  <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                  {m.model && (
                    <div className="mono mt-2 text-[9px] uppercase tracking-widest text-muted-foreground">
                      {m.model}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-2">
                <div className="flex h-7 w-7 items-center justify-center bg-foreground text-background">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Thinking…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-border bg-background p-3">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask your tutor…"
                rows={2}
                className="flex-1 resize-none border border-foreground bg-background p-2 text-sm focus:outline-none"
                data-testid="tutor-input"
              />
              <Button
                onClick={send}
                disabled={busy || !input.trim()}
                className="rounded-none bg-foreground text-background hard-shadow-sm"
                data-testid="tutor-send-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
